/**
 * GenerateCvOptionsSheet.jsx — RC 1.41.G4.G + RC 1.47.CV-DEFAULTS
 *
 * Pre-flight configuration modal for the Step 2 "Generate CV" CTA.
 *
 * Architecture (RC 1.47 Phase 1, 2026-05-02):
 * --------------------------------------------
 * The user-visible surface collapsed from three independent radio
 * groups (template / tone / length) down to ONE "CV style" picker
 * with three options:
 *
 *     • Standard   — workhorse, most roles, most readers
 *     • Executive  — leadership-led narrative, board-ready
 *     • ATS        — keyword-dense, scanner-friendly
 *
 * Each style maps to a hidden, pre-validated combination of the
 * three backend knobs `template_type` / `tone_type` /
 * `length_preference`. The mapping table lives in `STYLE_PRESETS`
 * below and is the single source of truth on the frontend. The
 * backend continues to validate every value strictly — the pre-set
 * combinations are picked from the existing whitelisted enums
 * (`routes/cv_variants.py::generate_cv_variant`) so this change is
 * purely a UX simplification: no new API surface, no prompt edit.
 *
 * Why this is safe:
 *   • All three preset values for each field are already in the
 *     backend allow-list (they were the OLD radio options).
 *   • The DB column shapes don't change (`template_type`,
 *     `tone_type`, `length_preference` columns on `cv_variants`).
 *   • Audit trail keeps recording the resolved low-level fields,
 *     so "what did the wizard generate this CV with" is still
 *     answerable per row.
 *   • Reject-gate, tolerance band, strict grading, charge
 *     governance — all read off resolved low-level fields, none
 *     touched.
 *
 * Hard rails (RC 1.41.G4.G):
 *   • Frontend-only. No new endpoint. No prompt change.
 *   • Cancel does NOT call /api/cv-variants/generate.
 *   • Confirm triggers exactly one onConfirm({template_type,
 *     tone_type, length_preference}) and closes.
 *   • The component is purely presentational + local-state. The
 *     parent owns the actual API call so this stays lightweight.
 */

import { useState } from 'react';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter,
} from '@/components/ui/sheet';
import { useCredits, getActionCost } from '@/lib/creditsContext';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Sparkles, Loader2 } from 'lucide-react';

// ─────────────────────────────────────────────────────────────────
// RC 1.47.CV-DEFAULTS — winning combinations.
//
// Each `style` row maps a single user-facing label to the hidden
// (template_type, tone_type, length_preference) trio the backend
// expects. These trios are NOT speculative: each combination is
// already in the backend allow-list and was selectable individually
// pre-RC 1.47.
//
// Mapping rationale (from CV-quality-lab observations + decision-
// table conventions):
//
//   Standard:  standard_professional + analytical + flexible
//     → Balanced, evidence-led, length follows experience depth.
//       Best for the majority of mid-/senior-IC roles.
//
//   Executive: executive + strategic + two_page
//     → Leadership-led narrative, outcomes-framed, room for the
//       multi-decision-cycle stories execs need to land.
//
//   ATS:       ats_compact + analytical + one_page
//     → Plain layout, dense keywords, evidence-led copy, ruthless
//       length cap so the screener sees signal-on-page-1.
//
// If/when the lab confirms a different winning combo, edit ONE row
// here. No backend change required.
// ─────────────────────────────────────────────────────────────────
const STYLE_PRESETS = Object.freeze({
  standard: {
    label: 'Standard',
    blurb: 'Balanced, evidence-led layout — most roles, most readers.',
    template_type:     'standard_professional',
    tone_type:         'analytical',
    length_preference: 'flexible',
  },
  executive: {
    label: 'Executive',
    blurb: 'Leadership-led, outcomes-framed — director / VP / C-suite roles.',
    template_type:     'executive',
    tone_type:         'strategic',
    length_preference: 'two_page',
  },
  ats: {
    label: 'ATS',
    blurb: 'Plain, keyword-dense, screener-friendly — for high-automation funnels.',
    template_type:     'ats_compact',
    tone_type:         'analytical',
    length_preference: 'one_page',
  },
});

const STYLE_OPTIONS = Object.entries(STYLE_PRESETS).map(([value, p]) => ({
  value,
  label: p.label,
  blurb: p.blurb,
}));

// RC 1.41 P0.1.D — additional CV context cap (mirrors backend
// constant in routes/cv_variants.py). Trim/length validation is
// also enforced server-side so a stale frontend can't bypass it.
const ADDITIONAL_CV_CONTEXT_MAX_CHARS = 4000;

// RC 1.49.7 pre-Phase-5 hardening (2026-02): default field set is
// hoisted to `@/lib/cvDefaults`. This file re-exports the same
// frozen object as `DEFAULTS` so the wizard's existing call sites
// (and any external test fixture importing `DEFAULTS` from this
// path) continue to compile unchanged. The workspace now imports
// from `@/lib/cvDefaults` directly, breaking its build-time edge
// to the wizard surface ahead of Phase 6 wizard deletion.
import { GEN_CV_DEFAULTS, DEFAULT_STYLE as _DEFAULT_STYLE } from '@/lib/cvDefaults';

const DEFAULT_STYLE = _DEFAULT_STYLE;
const DEFAULTS = GEN_CV_DEFAULTS;

function StyleGroup({ value, onChange }) {
  return (
    <div className="space-y-2">
      <p className="text-[10px] uppercase tracking-[0.18em] text-ndor-ink-muted font-mono-ui">
        CV style
      </p>
      <RadioGroup
        value={value}
        onValueChange={onChange}
        className="grid gap-1.5"
        data-testid="generate-cv-options-style-group"
      >
        {STYLE_OPTIONS.map(o => (
          <Label
            key={o.value}
            htmlFor={`generate-cv-options-style-${o.value}`}
            className={
              'flex items-start gap-2.5 rounded-md border px-3 py-2 cursor-pointer transition-colors ' +
              (value === o.value
                ? 'border-ndor-gold bg-ndor-gold/5'
                : 'border-ndor-edge hover:border-ndor-ink-muted')
            }
          >
            <RadioGroupItem
              value={o.value}
              id={`generate-cv-options-style-${o.value}`}
              data-testid={`generate-cv-options-style-${o.value}`}
              className="mt-0.5"
            />
            <span className="space-y-0.5">
              <span className="block text-sm font-medium text-ndor-ink leading-tight">{o.label}</span>
              <span className="block text-[11px] text-ndor-ink-secondary leading-relaxed">{o.blurb}</span>
            </span>
          </Label>
        ))}
      </RadioGroup>
    </div>
  );
}

export default function GenerateCvOptionsSheet({
  open, onOpenChange, onConfirm, generating = false,
}) {
  const [style, setStyle] = useState(DEFAULTS.style);
  // RC 1.41 P0.1.D — optional candidate-supplied "what's new since
  // the Source CV" payload. Empty/whitespace-only = old behaviour.
  const [extraContext, setExtraContext] = useState(DEFAULTS.additional_cv_context);

  const { usageCosts } = useCredits();
  const cvCost = getActionCost(usageCosts, 'cv_improve');
  const cvLabel = cvCost == null
    ? ''
    : ` · up to ${cvCost} ${cvCost === 1 ? 'Credit' : 'Credits'}`;

  const reset = () => {
    setStyle(DEFAULTS.style);
    setExtraContext(DEFAULTS.additional_cv_context);
  };

  const handleOpenChange = (v) => {
    if (!v) reset();
    if (typeof onOpenChange === 'function') onOpenChange(v);
  };

  const trimmedContext = (extraContext || '').trim();
  const overCap = trimmedContext.length > ADDITIONAL_CV_CONTEXT_MAX_CHARS;
  const charsLeft = Math.max(0, ADDITIONAL_CV_CONTEXT_MAX_CHARS - trimmedContext.length);

  const handleConfirm = () => {
    if (overCap) return;
    // RC 1.47.CV-DEFAULTS — resolve the chosen style to the three
    // low-level backend fields BEFORE leaving this component. The
    // wizard / API call site never sees the new `style` token, so
    // back-compat is total: every existing telemetry, audit, lab
    // and test path still works against {template_type, tone_type,
    // length_preference}.
    const preset = STYLE_PRESETS[style] || STYLE_PRESETS[DEFAULT_STYLE];
    onConfirm({
      template_type:     preset.template_type,
      tone_type:         preset.tone_type,
      length_preference: preset.length_preference,
      additional_cv_context: trimmedContext,
    });
    // Parent closes the sheet on success/failure.
  };

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent
        side="right"
        className="sm:max-w-[420px] flex flex-col gap-4"
        data-testid="generate-cv-options-sheet"
      >
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-ndor-gold" />
            Configure your tailored CV
          </SheetTitle>
          <SheetDescription className="text-xs leading-relaxed">
            Pick a CV style. NDORFlow applies the validated tone and
            length defaults for that style. You can preview the
            result before locking it into your application pack.
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto space-y-5 pr-1">
          <StyleGroup value={style} onChange={setStyle} />

          {/* RC 1.41 P0.1.D — Additional CV context. Optional free
              text the candidate can supply when their Source CV is
              out-of-date (recent achievements, role changes, certs,
              metrics, projects). Forwarded to the LLM as a discrete
              input block; empty/whitespace-only = old behaviour. */}
          <div className="space-y-2" data-testid="generate-cv-options-additional-context">
            <p className="text-[10px] uppercase tracking-[0.18em] text-ndor-ink-muted font-mono-ui">
              Additional CV context · optional
            </p>
            <p className="text-[11px] text-ndor-ink-secondary leading-relaxed">
              Add recent achievements, role changes, certifications, projects, metrics, or anything NDORFlow should consider when improving your CV.
            </p>
            <textarea
              value={extraContext}
              onChange={(e) => setExtraContext(e.target.value)}
              placeholder="Example: Recently led a $50m infrastructure evaluation, completed a new certification, managed a team of analysts, or want to emphasise business development experience."
              rows={5}
              maxLength={ADDITIONAL_CV_CONTEXT_MAX_CHARS + 200}
              disabled={generating}
              data-testid="generate-cv-options-additional-context-input"
              className="w-full rounded-md border border-ndor-edge bg-background text-sm text-ndor-ink leading-relaxed p-2.5 focus:outline-none focus:ring-2 focus:ring-ndor-gold/40 disabled:opacity-50 disabled:cursor-not-allowed"
            />
            <div className="flex items-center justify-between text-[10px] text-ndor-ink-muted font-mono-ui">
              <span data-testid="generate-cv-options-additional-context-charcount">
                {trimmedContext.length} / {ADDITIONAL_CV_CONTEXT_MAX_CHARS} chars
              </span>
              {overCap ? (
                <span className="text-ndor-error" data-testid="generate-cv-options-additional-context-error">
                  Trim by {trimmedContext.length - ADDITIONAL_CV_CONTEXT_MAX_CHARS} chars to continue.
                </span>
              ) : trimmedContext.length === 0 ? (
                <span>Empty — generation behaves as before.</span>
              ) : (
                <span>{charsLeft} chars left.</span>
              )}
            </div>
          </div>
        </div>

        <SheetFooter className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
          <Button
            variant="ghost"
            onClick={() => handleOpenChange(false)}
            disabled={generating}
            data-testid="generate-cv-options-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={generating || overCap}
            data-testid="generate-cv-options-confirm"
          >
            {generating
              ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
              : <Sparkles className="h-3.5 w-3.5 mr-1.5" />}
            Generate{cvLabel}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

// Re-exported for the wizard's variant-naming suffix logic and any
// pin tests that need the canonical preset table.
//
// `TEMPLATES`, `TONES`, `LENGTHS` continue to be exported for
// back-compat with existing test files; they are derived from
// `STYLE_PRESETS` so a future contributor can't drift them.
const TEMPLATES = Object.values(STYLE_PRESETS).map(p => ({
  value: p.template_type, label: p.label, blurb: p.blurb,
}));
const TONES = Array.from(new Set(Object.values(STYLE_PRESETS).map(p => p.tone_type)))
  .map(v => ({ value: v, label: v.charAt(0).toUpperCase() + v.slice(1) }));
const LENGTHS = Array.from(new Set(Object.values(STYLE_PRESETS).map(p => p.length_preference)))
  .map(v => ({ value: v, label: v.replace(/_/g, ' ') }));

export {
  STYLE_PRESETS, STYLE_OPTIONS, DEFAULT_STYLE,
  TEMPLATES, TONES, LENGTHS, DEFAULTS,
};
